import React, { useMemo, useState } from 'react';
import FiltrosPanel, { CAMPO_LARGO, CAMPO } from '../../components/FiltrosPanel';
import PaginacaoFooter from '../../components/PaginacaoFooter';
import {
    Box,
    Grid,
    useTheme,
    useMediaQuery,
    TextField,
    InputAdornment,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import LoadingOverlay from '../../components/LoadingOverlay';
import ProtocolMicrobi from '../../components/ProtocolsMicrobi';
import ErrorDisplay from '../../components/ErrorDisplay';
import { SnackbarAlert } from '../../components/SnackbarAlert';
import { useBootstrap } from '../../context/BootstrapContext';
import JsonViewerDialog from '../../components/JsonViewerDialog';

const MicrobiologicalAnalysisPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { bootstrap } = useBootstrap();
    const protocolos = useMemo(() => bootstrap?.protocolos ?? [], [bootstrap]);
    const [loading] = useState(false);
    const [errors] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [itemsPerPage, setItemsPerPage] = useState(6);
    const [currentPage, setCurrentPage] = useState(1);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const [protocolForJson, setProtocolForJson] = useState(null);

    const handleShowJson = (protocol) => {
        setProtocolForJson(protocol);
    };

    const handleCloseJson = () => {
        setProtocolForJson(null);
    };

    const filtered = useMemo(() => {
        return protocolos.filter((p) => {
            const matchesSearch = p.name
                .toLowerCase()
                .includes(searchTerm.toLowerCase());
            const matchesType = selectedType ? p.type === selectedType : true;
            return p.active && matchesSearch && matchesType;
        });
    }, [protocolos, searchTerm, selectedType]);

    const filtrosAtivos = (searchTerm ? 1 : 0) + (selectedType ? 1 : 0);

    const handleLimpar = () => {
        setSearchTerm('');
        setSelectedType('');
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const paginatedProtocols = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filtered.slice(startIndex, startIndex + itemsPerPage);
    }, [filtered, currentPage, itemsPerPage]);

    const protocolTypes = useMemo(
        () => [...new Set(protocolos.map((p) => p.type).filter(Boolean))],
        [protocolos]
    );

    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    if (loading) return <LoadingOverlay />;
    if (errors.length) return <ErrorDisplay errors={errors} />;

    return (
        <Box
            sx={{
                px: { xs: 2, sm: 3, md: 4 },
                // Sem o titulo (a navbar ja mostra a pagina), o topo pede menos respiro.
                pt: { xs: 2, sm: 2.5 },
                pb: { xs: 2, sm: 3, md: 4 },
            }}
        >

            <FiltrosPanel
                defaultExpanded
                ativos={filtrosAtivos}
                onLimpar={handleLimpar}
            >
                <Grid item {...CAMPO_LARGO}>
                    <TextField
                        fullWidth
                        label="Buscar por nome"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Lucide.Search size={18} />
                                </InputAdornment>
                            ),
                        }}
                        size="small"
                    />
                </Grid>
                <Grid item {...CAMPO}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Tipo</InputLabel>
                        <Select
                            value={selectedType}
                            label="Tipo"
                            onChange={(e) => setSelectedType(e.target.value)}
                        >
                            <MenuItem value="">
                                <em>Todos os Tipos</em>
                            </MenuItem>
                            {protocolTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </FiltrosPanel>

            <Grid container spacing={isMobile ? 2 : 3}>
                {paginatedProtocols.map((protocol) => (
                    <Grid item xs={12} sm={6} md={4} key={protocol.id}>
                        <ProtocolMicrobi
                            protocol={protocol}
                            onShowJson={handleShowJson}
                        />
                    </Grid>
                ))}
            </Grid>

            <PaginacaoFooter
                page={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={(n) => {
                    setItemsPerPage(n);
                    setCurrentPage(1);
                }}
                itemsPerPageOptions={[6, 12, 24, 48]}
            />


            <JsonViewerDialog
                open={!!protocolForJson}
                protocol={protocolForJson}
                onClose={handleCloseJson}
            />

            <SnackbarAlert
                open={snackbar.open}
                onClose={handleCloseSnackbar}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Box>
    );
};

export default MicrobiologicalAnalysisPage;
